import React, { useRef, useEffect, useState } from 'react';
import styles from './FeatureBlockSection.module.scss';
import FeatureItem from '../components/FeatureItem';

const LazyVideo = ({ src, poster, className }) => {
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);

          // Start loading when visible for the first time
          if (entry.isIntersecting && !hasLoaded) {
            setHasLoaded(true);
            video.load();
          }

          // Play/pause based on visibility
          if (entry.isIntersecting && hasLoaded) {
            video.play().catch(() => {});
          } else if (!entry.isIntersecting) {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [hasLoaded]);

  return (
    <video
      ref={videoRef}
      className={className}
      muted
      loop
      playsInline
      preload="none"
      poster={poster}
    >
      {hasLoaded && <source src={src} type="video/mp4" />}
    </video>
  );
};

const FeatureBlockSection = ({
  title,
  description,
  features,
  featuresLabel = 'features',
  image,
  imageAlt = 'Feature illustration',
  video,
  videoPoster,
  reverse = false,
  backgroundColor = '#FFFFFF',
}) => {
  return (
    <section className={styles.featureBlock} style={{ background: backgroundColor }}>
      <div className={`${styles.container} ${reverse ? styles.reverse : ''}`}>
        <div className={styles.content}>
          <div className={styles.textContent}>
            <h3 className={styles.title}>{title}</h3>
            <p className={styles.description}>{description}</p>
          </div>

          <div className={styles.featuresBox}>
            <span className={styles.featuresLabel}>{featuresLabel}</span>
            <div className={styles.featuresList}>
              {features.map((feature, index) => (
                <FeatureItem key={index} icon={feature.icon}>
                  {feature.text}
                </FeatureItem>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.imageWrapper}>
          {video ? (
            <LazyVideo
              src={video}
              poster={videoPoster}
              className={styles.video}
            />
          ) : image ? (
            <img src={image} alt={imageAlt} className={styles.image} />
          ) : (
            <div className={styles.imagePlaceholder}>
              <span>Feature Illustration</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureBlockSection;
